import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, resolveComponent as _resolveComponent, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, withModifiers as _withModifiers, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "profile content" }
const _hoisted_2 = { class: "profile__grid" }
const _hoisted_3 = { class: "profile__grid-item" }
const _hoisted_4 = { class: "profile__max-width" }
const _hoisted_5 = { class: "profile__info" }
const _hoisted_6 = { class: "profile__info" }
const _hoisted_7 = /*#__PURE__*/_createElementVNode("div", { class: "profile__grid-item" }, null, -1)
const _hoisted_8 = { class: "profile__grid-item" }
const _hoisted_9 = ["onSubmit"]
const _hoisted_10 = ["onSubmit"]

import { ref, computed } from 'vue'

// Components
import Avatar from '@/components/ui/Avatar'
import EarnedCard from '@/components/EarnedCard'
import Cards from '@/components/Cards'
import Modal from '@/components/ui/Modal'
import Button from '@/components/ui/Button'
import Title from '@/components/ui/Title'
import InputText from '@/components/inputs/InputText'
import { ElNotification } from 'element-plus'

// i18n
import { useI18n } from 'vue-i18n'
import { useCommonStore, useProfileStore } from '@/store'
import { getAxiosErrorText, getExtension } from '@/common/helpers'
import { IdType } from '@/common/types'
const acceptAvatar = '.gif, .jpeg, .jpg, .png'
const maxFileSizeAvatar = 26214400 // 25 MB

export default /*#__PURE__*/_defineComponent({
  __name: 'Profile',
  setup(__props) {

const { t } = useI18n()

// Data
const name = ref<string>('')
const uploadAvatarLoading = ref<boolean>(false)
const input = ref()
const card = ref<string>('')

// Store
const commonStore = useCommonStore()
const profileStore = useProfileStore()

// Computed
const isNameValid = computed<boolean>(() => Boolean(name.value.length >= 2))
const isCardValid = computed<boolean>(() => Boolean(card.value.length >= 19))

// Methods
function closeModal(): void {
  profileStore.modal.show = false
  profileStore.modal.display = null
  profileStore.modal.loading = false
}

function editName(): void {
  name.value = commonStore.user?.name ?? ''
  profileStore.modal.show = true
  profileStore.modal.display = 'name'
}

function error(e: any) {
  ElNotification({
    title: t('common.error'),
    message: getAxiosErrorText(e),
    type: 'error',
  })
}

async function addCard() {
  profileStore.modal.show = true
  profileStore.modal.display = 'card'
}

async function tryToAddCard() {
  profileStore.modal.loading = true
  try {
    await profileStore.addNewCard({ card: card.value })
    commonStore.user = await commonStore.getUser()
    card.value = ''
    closeModal()
  } catch (e: any) {
    error(e)
  } finally {
    profileStore.modal.loading = false
  }
}

async function tryToChangeName() {
  profileStore.modal.loading = true
  try {
    await profileStore.changeName({ name: name.value })
    commonStore.user = await commonStore.getUser()
    closeModal()
  } catch (e: any) {
    error(e)
  } finally {
    profileStore.modal.loading = false
  }
}

async function tryUploadAvatar(event: Event) {
  const target = event.target as HTMLInputElement
  let file = target.files?.[0] ?? null
  if (!file?.name) {
    return
  }

  const ext = getExtension(file!.name).toLowerCase() ?? null
  const isAllow = acceptAvatar.includes(ext)
  let hasError = false

  if (!isAllow) {
    error(t('profile.extError'))
    hasError = true
  }

  if (file.size && file.size > maxFileSizeAvatar) {
    error(t('profile.maxSizeError'))
    hasError = true
  }

  if (!hasError) {
    uploadAvatarLoading.value = true
    try {
      await profileStore.uploadAvatar({ file })
      commonStore.user = await commonStore.getUser()
      closeModal()
    } catch (e: any) {
      error(e)
    } finally {
      uploadAvatarLoading.value = false

    }
  }
}

async function setDefault(id: IdType) {
  commonStore.loading = true
  try {
    await profileStore.setDefaultCard(id)
    commonStore.user = await commonStore.getUser()
  } catch (e: any) {
    error(e)
  } finally {
    commonStore.loading = false
  }
}

async function deleteCard(id: IdType) {
  commonStore.loading = true
  try {
    await profileStore.deleteCard(id)
    commonStore.user = await commonStore.getUser()
  } catch (e: any) {
    error(e)
  } finally {
    commonStore.loading = false
  }
}

return (_ctx: any,_cache: any) => {
  const _component_draw_svg = _resolveComponent("draw-svg")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createVNode(_unref(Title), {
        class: "profile__title",
        theme: "huge"
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_ctx.$t('profile.title')), 1)
        ]),
        _: 1
      }),
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("div", {
              class: _normalizeClass(["profile__avatar", {'profile__avatar_loading': uploadAvatarLoading.value}])
            }, [
              _createVNode(_unref(Avatar), {
                picture: _unref(commonStore).user?.avatar,
                theme: "huge"
              }, null, 8, ["picture"]),
              _createVNode(_unref(Button), {
                theme: ['black', 'icon-padding'],
                class: "profile__upload-button"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_draw_svg, {
                    name: "upload",
                    width: "32",
                    height: "32"
                  })
                ]),
                _: 1
              }),
              _createElementVNode("input", {
                ref_key: "input",
                ref: input,
                id: "avatar-upload",
                type: "file",
                onChange: tryUploadAvatar,
                accept: acceptAvatar
              }, null, 544)
            ], 2),
            _createElementVNode("div", _hoisted_5, [
              _createElementVNode("span", null, _toDisplayString(_ctx.$t('profile.name')), 1),
              _createElementVNode("span", null, _toDisplayString(_unref(commonStore).user?.name), 1),
              _createVNode(_unref(Button), { onClick: editName }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('profile.edit')), 1)
                ]),
                _: 1
              })
            ]),
            _createElementVNode("div", _hoisted_6, [
              _createElementVNode("span", null, _toDisplayString(_ctx.$t('profile.tg')), 1),
              _createElementVNode("span", null, "@" + _toDisplayString(_unref(commonStore).user?.tg_username), 1)
            ])
          ])
        ]),
        _hoisted_7,
        _createElementVNode("div", _hoisted_8, [
          _createVNode(_unref(EarnedCard), { class: "profile__earned-card" }),
          _createVNode(_unref(Cards), {
            onAdd: addCard,
            onSetDefault: setDefault,
            onDelete: deleteCard
          })
        ])
      ])
    ]),
    (_unref(profileStore).modal.show)
      ? (_openBlock(), _createBlock(_unref(Modal), {
          key: 0,
          theme: "top",
          onClose: closeModal,
          loading: _unref(profileStore).modal.loading
        }, {
          default: _withCtx(() => [
            (_unref(profileStore).modal.display === 'name')
              ? (_openBlock(), _createElementBlock("form", {
                  key: 0,
                  class: "profile__modal-form",
                  onSubmit: _withModifiers(tryToChangeName, ["prevent"])
                }, [
                  _createVNode(_unref(Title), { theme: ['modal', 'centered'] }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_ctx.$t('profile.changeNameTitle')), 1)
                    ]),
                    _: 1
                  }),
                  _createElementVNode("div", null, [
                    _createVNode(_unref(InputText), {
                      name: "name",
                      modelValue: name.value,
                      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((name).value = $event)),
                      focus: "",
                      label: _ctx.$t('profile.newName')
                    }, null, 8, ["modelValue", "label"])
                  ]),
                  _createElementVNode("div", null, [
                    _createVNode(_unref(Button), {
                      theme: ['stretched', 'huge'],
                      disabled: !isNameValid.value
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_ctx.$t('profile.submit')), 1)
                      ]),
                      _: 1
                    }, 8, ["disabled"])
                  ])
                ], 40, _hoisted_9))
              : _createCommentVNode("", true),
            (_unref(profileStore).modal.display === 'card')
              ? (_openBlock(), _createElementBlock("form", {
                  key: 1,
                  class: "profile__modal-form",
                  onSubmit: _withModifiers(tryToAddCard, ["prevent"])
                }, [
                  _createVNode(_unref(Title), { theme: ['modal', 'centered'] }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_ctx.$t('profile.addCardTitle')), 1)
                    ]),
                    _: 1
                  }),
                  _createElementVNode("div", null, [
                    _createVNode(_unref(InputText), {
                      name: "name",
                      modelValue: card.value,
                      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((card).value = $event)),
                      focus: "",
                      mask: "card",
                      label: _ctx.$t('profile.newCard')
                    }, null, 8, ["modelValue", "label"])
                  ]),
                  _createElementVNode("div", null, [
                    _createVNode(_unref(Button), {
                      theme: ['stretched', 'huge'],
                      disabled: !isCardValid.value
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_ctx.$t('profile.submit')), 1)
                      ]),
                      _: 1
                    }, 8, ["disabled"])
                  ])
                ], 40, _hoisted_10))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }, 8, ["loading"]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})